import { fetchAuthSession } from 'aws-amplify/auth';
import { ENV } from '../amplify-config';
import type { PortalZone } from './zones';

/**
 * Minimal staff-API client for the `/admin/*` routes.
 *
 * ⚠ POST-ADR-099 NAMING: `/admin/*` is served by the NINE CARVED SERVICES, not by a monolith —
 * `lambdas/admin-api` was DELETED (W6b). The `adminApi` function name and `ADMIN_API_BASE_URL`
 * are retained because they name the URL PREFIX (which is unchanged), not a service. What was
 * removed is the literal string `admin-api` from the thrown message, which was leaking a
 * deleted service's name into user-visible toasts (punchlist #21).
 */

/**
 * A failed `/admin/*` call, carrying the machine-readable pieces a UI needs to map the failure
 * to a friendly message.
 *
 * ⭐ `code` IS THE POINT. Before this, callers had only a formatted string and matched it with
 * `msg.includes('stale_auth')` — which works until an unmapped code arrives, at which point the
 * raw string was shown to the user verbatim (punchlist #21: `admin-api 409 on
 * /admin/subscribers/<id>/memberships: {"error":"membership_exists"}`). `message` still embeds
 * the code so those existing `.includes()` call sites keep working unchanged; new code should
 * read `.code`.
 *
 * ⚠ `message` is NOT user-facing. It is diagnostic — log it, never render it.
 */
export class StaffApiError extends Error {
  readonly status: number;
  /** The server's `error.code` (e.g. `membership_exists`), or '' when unparseable. */
  readonly code: string;
  readonly path: string;
  /**
   * The server's request id — the operator's REFERENCE (6 October 2026). The account service
   * stamps it on every staff error; support finds the request in CloudWatch by it. '' when the
   * response carried none (another service, a gateway error, or no response at all).
   */
  readonly requestId: string;
  /**
   * The refusal's other fields (e.g. `invalid_transition`'s `from` / `to`, the budget block's
   * `message`) — read by the friendly mapper to say WHY in words. Never rendered raw.
   */
  readonly details: Readonly<Record<string, unknown>>;

  constructor(
    status: number,
    path: string,
    code: string,
    rawBody: string,
    requestId = '',
    details: Record<string, unknown> = {},
  ) {
    super(`staff-api ${status} on ${path}: ${code || rawBody}`);
    this.name = 'StaffApiError';
    this.status = status;
    this.code = code;
    this.path = path;
    this.requestId = requestId;
    this.details = details;
  }
}

interface ParsedErrorBody {
  code: string;
  requestId: string;
  details: Record<string, unknown>;
}

/** Pull `error.code` (and the request id + any detail fields) out of a response body, tolerating
 *  both the `{error:{code}}` envelope and the older flat `{error:"code"}` shape. The code is ''
 *  when the body is not JSON. */
export function parseErrorBody(body: string): ParsedErrorBody {
  try {
    const parsed = JSON.parse(body) as Record<string, unknown> & {
      error?: string | { code?: string };
      code?: string;
      requestId?: unknown;
    };
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { code: '', requestId: '', details: {} };
    }
    const { error, code, requestId, ...rest } = parsed;
    let c = '';
    if (typeof error === 'string') c = error;
    else if (error && typeof error === 'object' && error.code) c = error.code;
    else c = typeof code === 'string' ? code : '';
    return { code: c, requestId: typeof requestId === 'string' ? requestId : '', details: rest };
  } catch {
    return { code: '', requestId: '', details: {} };
  }
}

/**
 * Client-side request timeout (ms). WHY THIS EXISTS (ADR-103 S4d, T14 UAT #9).
 *
 * The Flow Validator Refresh button "stopped being clickable for an unknown
 * reason." Root cause: every loader sets a `loading` flag true, fires this
 * fetch, and only clears the flag in a `finally`. With NO client timeout, a
 * request that never settles — a stalled network, a cold-start that outlives the
 * browser's patience, a connection a proxy holds open — leaves that promise
 * pending forever, so `finally` never runs and the button stays `disabled`
 * permanently. A bounded timeout makes a hung request REJECT, so the loader's
 * catch/finally runs and the control re-enables. 30s is well past any healthy
 * `/admin/*` call (incl. a Lambda cold start) yet bounds the wedge.
 */
const ADMIN_API_TIMEOUT_MS = 30_000;

/**
 * Call `/admin/*` in a SPECIFIC zone (ADR-120 P8 step 7(b)).
 *
 * ⭐ THE SAME TOKEN IS SENT TO EVERY ZONE, AND THAT IS THE WHOLE MECHANISM. Since ADR-120
 * Amendment 2 there is ONE staff Cognito pool for the platform, so every zone's admin-API
 * authorizer trusts the same issuer and one operator's token is accepted everywhere. Nothing
 * zone-specific is added to the request — only the origin changes.
 *
 * Used for the LIST reads that fan out. Single-record reads and every mutation stay on the
 * home zone via `adminApi`, because a record is only ever in one zone and the caller reached
 * it from that zone's row.
 */
export async function adminApiInZone<T>(
  zone: PortalZone,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return adminApiAt(zone.adminApiBaseUrl, path, init);
}

export async function adminApi<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return adminApiAt(ENV.ADMIN_API_BASE_URL, path, init);
}

async function adminApiAt<T>(
  baseUrl: string,
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new StaffApiError(401, path, 'no_id_token', 'no ID token — sign in first');
  }
  const url = `${baseUrl}${path}`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      ...init,
      // A caller-supplied signal wins; otherwise bound every call so a hung
      // request can never wedge a loading flag (S4d). AbortSignal.timeout fires
      // an AbortError, which surfaces as a friendly toast, not a stuck button.
      signal: init.signal ?? AbortSignal.timeout(ADMIN_API_TIMEOUT_MS),
      headers: {
        ...(init.headers ?? {}),
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (err) {
    // A timeout/abort or a network failure — reject with a typed error so the
    // caller's catch/finally runs (re-enabling the control) and a friendly
    // message can be mapped. Never leaves the promise pending.
    const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
    throw new StaffApiError(
      timedOut ? 504 : 0,
      path,
      timedOut ? 'request_timeout' : 'network_error',
      err instanceof Error ? err.message : 'request failed',
    );
  }
  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    const parsed = parseErrorBody(body);
    throw new StaffApiError(resp.status, path, parsed.code, body, parsed.requestId, parsed.details);
  }
  return (await resp.json()) as T;
}
